import {
  useRef,
  useState,
  type ReactNode,
  type PropsWithChildren,
} from "react";
import { Link, useForm, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { PageCanvas, PageHeader } from "@/components/page-canvas";
import CaseWorkspace, { CaseNavigation } from "@/components/case-workspace";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { FormErrors } from "@/components/forms/form-errors";
import type { BreadcrumbItem } from "@/types";
import type { JobContext } from "@/types/job-context";
import {
  locationJobPath,
  locationJobBoardPath,
  locationJobPaymentPostingsPath,
  locationJobFiscalDocumentsPath,
  locationJobProductConsumptionsPath,
  locationJobActivityPath,
  locationJobProgressPath,
  newLocationJobPaymentPostingPath,
  locationRecordingOperationalNotificationsPath,
} from "@/routes";
import { dateTime, money } from "@/pages/jobs/types";

export function jobTab(url: string) {
  if (url.includes("/payment_postings") || url.includes("/advance_requests"))
    return "payments";
  if (url.includes("/fiscal_documents")) return "documents";
  if (url.includes("/product_consumptions")) return "products";
  if (url.includes("/activity") || url.includes("/operational_notifications"))
    return "activity";
  if (url.includes("/billing_reviews")) return "billing";
  if (
    url.includes("/reschedules") ||
    new URLSearchParams(url.split("?")[1]).get("section") === "schedule"
  )
    return "schedule";
  return new URLSearchParams(url.split("?")[1]).get("section") === "billing"
    ? "billing"
    : "overview";
}

function LifecycleAction({
  job,
  section,
  action,
  secondary = false,
}: {
  job: JobContext;
  section: string;
  action: "start" | "complete" | "bill" | "collect";
  secondary?: boolean;
}) {
  const { auth } = usePage().props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const form = useForm({
    progress: {
      action: action || "",
      revision: job.revision,
      send_arrival: "",
      billing_assignee_id: "",
    },
    return_section: section,
  });
  if (!auth.location || !job.actions[action]) return null;
  return (
    <>
      <Button
        variant={secondary ? "outline" : "default"}
        ref={trigger}
        onClick={() => {
          form.setData({
            progress: {
              action,
              revision: job.revision,
              send_arrival: "",
              billing_assignee_id: "",
            },
            return_section: section,
          });
          form.clearErrors();
          setOpen(true);
        }}
      >
        {t(`job_board.${action}`)}
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          className="w-full! overflow-y-auto sm:max-w-lg!"
          finalFocus={trigger}
        >
          <SheetHeader>
            <SheetTitle>{t(`job_board.${action}`)}</SheetTitle>
            <SheetDescription>{t("workspace.action_help")}</SheetDescription>
          </SheetHeader>
          <form
            className="space-y-5 px-4 pb-4"
            onSubmit={(event) => {
              event.preventDefault();
              form.patch(locationJobProgressPath(auth.location!.id, job.id), {
                preserveScroll: true,
                onSuccess: () => setOpen(false),
              });
            }}
          >
            <FormErrors
              errors={form.errors}
              targets={{
                send_arrival: "next-arrival",
                billing_assignee: "next-billing",
              }}
            />
            {action === "start" && (
              <div className="space-y-2">
                <Label htmlFor="next-arrival">
                  {t("job_board.send_arrival")}
                </Label>
                <NativeSelect
                  id="next-arrival"
                  required
                  value={form.data.progress.send_arrival}
                  onChange={(event) =>
                    form.setData("progress", {
                      ...form.data.progress,
                      send_arrival: event.target.value,
                    })
                  }
                  className="w-full"
                >
                  <NativeSelectOption value="">
                    {t("jobs.choose")}
                  </NativeSelectOption>
                  <NativeSelectOption value="true">
                    {t("job_board.send_yes")}
                  </NativeSelectOption>
                  <NativeSelectOption value="false">
                    {t("job_board.send_no")}
                  </NativeSelectOption>
                </NativeSelect>
              </div>
            )}
            {action === "bill" && (
              <div className="space-y-2">
                <Label htmlFor="next-billing">
                  {t("job_board.billing_assignee")}
                </Label>
                <NativeSelect
                  id="next-billing"
                  required
                  value={form.data.progress.billing_assignee_id}
                  onChange={(event) =>
                    form.setData("progress", {
                      ...form.data.progress,
                      billing_assignee_id: event.target.value,
                    })
                  }
                  className="w-full"
                >
                  <NativeSelectOption value="">
                    {t("jobs.choose")}
                  </NativeSelectOption>
                  {job.billing_staff.map((person) => (
                    <NativeSelectOption key={person.id} value={person.id}>
                      {person.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </div>
            )}
            {action === "complete" && <p>{t("workspace.complete_help")}</p>}
            {action === "collect" && <p>{t("job_board.collection_help")}</p>}
            <div className="flex flex-wrap gap-3">
              <Button type="submit" disabled={form.processing}>
                {t(`job_board.${action}`)}
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={() => setOpen(false)}
              >
                {t("common.cancel")}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}

function NextAction({ job, section }: { job: JobContext; section: string }) {
  const { auth } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const action = job.next_action;
  let primary: ReactNode = null;
  if (
    action === "start" ||
    action === "complete" ||
    action === "bill" ||
    action === "collect"
  ) {
    primary = <LifecycleAction job={job} section={section} action={action} />;
  } else if (action === "record_payment" && job.actions.record_payment) {
    primary = (
      <Button
        render={
          <Link
            href={newLocationJobPaymentPostingPath(auth.location.id, job.id)}
          />
        }
      >
        {t("payments.record")}
      </Button>
    );
  } else if (action === "review_billing" && section !== "billing") {
    primary = (
      <Button
        render={
          <Link
            href={locationJobPath(auth.location.id, job.id, {
              section: "billing",
            })}
          />
        }
      >
        {t("workspace.review_billing")}
      </Button>
    );
  }
  if (!primary && !job.actions.collect) return null;
  return (
    <div className="flex flex-wrap gap-3" data-job-actions="">
      {primary}
      {job.actions.collect && action !== "collect" && (
        <LifecycleAction
          job={job}
          section={section}
          action="collect"
          secondary
        />
      )}
    </div>
  );
}

export default function JobWorkspace({
  children,
  breadcrumbs = [],
  primaryAction,
}: PropsWithChildren<{
  breadcrumbs?: BreadcrumbItem[];
  primaryAction?: ReactNode | false;
}>) {
  const {
    url,
    props: { auth, i18n, job_context: job, notification_parent: parent },
  } = usePage();
  const { t } = useTranslation();
  if (!job || !auth.location)
    return (
      <AppLayout
        breadcrumbs={
          parent
            ? [{ title: parent.label, href: parent.href }, ...breadcrumbs]
            : breadcrumbs
        }
      >
        {parent && (
          <div className="px-page pt-page">
            <Link className="underline" href={parent.href}>
              {t("workspace.back_parent")}: {parent.label}
            </Link>
          </div>
        )}
        {children}
      </AppLayout>
    );
  const location = auth.location.id;
  const tab = jobTab(url);
  const tabs = [
    ["overview", locationJobPath(location, job.id)],
    ["schedule", locationJobPath(location, job.id, { section: "schedule" })],
    ["billing", locationJobPath(location, job.id, { section: "billing" })],
    ["payments", locationJobPaymentPostingsPath(location, job.id)],
    ["documents", locationJobFiscalDocumentsPath(location, job.id)],
    ["products", locationJobProductConsumptionsPath(location, job.id)],
    ["activity", locationJobActivityPath(location, job.id)],
  ];
  return (
    <AppLayout
      breadcrumbs={[
        { title: t("jobs.title"), href: locationJobBoardPath(location) },
        {
          title: `${job.vehicle.make} ${job.vehicle.model}`,
          href: locationJobPath(location, job.id),
        },
      ]}
    >
      <PageCanvas className="space-y-5 sm:space-y-section">
        <PageHeader
          title={`${job.vehicle.make} ${job.vehicle.model}`}
          description={
            <>
              {t("jobs.confirmed")} · {job.customer.name}
              {job.vehicle.registration_number
                ? ` · ${job.vehicle.registration_number}`
                : ""}
            </>
          }
          actions={
            primaryAction === false
              ? undefined
              : (primaryAction ?? <NextAction job={job} section={tab} />)
          }
        />
        <CaseWorkspace
          context={
            <div
              className="grid grid-cols-2 gap-4 xl:grid-cols-4"
              data-job-context={job.id}
            >
              <div className="min-w-0 space-y-2 break-words">
                <Badge>{t(`job_board.stages.${job.work_state}`)}</Badge>
                <p className="text-sm">
                  {job.collected_at
                    ? t("job_board.collected")
                    : t("jobs.not_collected")}
                </p>
              </div>
              <div className="min-w-0 break-words text-sm">
                <p className="font-medium">{job.bay_name}</p>
                <p>{dateTime(job.starts_at, job.time_zone, i18n.locale)}</p>
                <p className="text-muted-foreground">{job.time_zone}</p>
              </div>
              <div className="min-w-0 break-words text-sm">
                <p className="text-muted-foreground">{t("jobs.assignee_id")}</p>
                <p>
                  {
                    job.current_staff.find(
                      (person) => person.id === job.assignee_id,
                    )?.name
                  }
                </p>
                <p>
                  {job.current_staff
                    .filter((person) => job.helper_ids.includes(person.id))
                    .map((person) => person.name)
                    .join(", ")}
                </p>
              </div>
              <div className="min-w-0 break-words text-sm">
                <p className="text-muted-foreground">
                  {t("workspace.balance")}
                </p>
                <p className="font-semibold">
                  {money(job.outstanding_minor, job.currency, i18n.locale)}
                </p>
                <p>
                  {t("job_board.received", {
                    amount: money(
                      job.payment_received_minor,
                      job.currency,
                      i18n.locale,
                    ),
                  })}
                </p>
              </div>
            </div>
          }
          navigation={
            <CaseNavigation
              items={tabs.map(([key, href]) => ({
                id: key,
                label: t(`workspace.tabs.${key}`),
                href,
              }))}
              active={tab}
              label={t("workspace.navigation")}
            />
          }
          editor={false}
          fluid={!["overview", "schedule"].includes(tab)}
        >
          <div id="workspace-content" className="min-w-0">
            {children}
          </div>
        </CaseWorkspace>
        <Link
          className="inline-flex min-h-11 items-center text-sm underline"
          href={locationRecordingOperationalNotificationsPath(location, job.id)}
        >
          {t("notifications.history")}
        </Link>
      </PageCanvas>
    </AppLayout>
  );
}
